import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Check, X as XIcon, Plus, Trash2 } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const lakh = (v) => {
    const x = Number(v || 0);
    if (x >= 10000000) return `${(x / 10000000).toFixed(0)}Cr`;
    if (x >= 100000) return `${(x / 100000).toFixed(0)}L`;
    return inr(x);
};

const CALL_TIMES = ["Today morning (9-12)", "Today afternoon (12-3)", "Today evening (3-6)", "Tomorrow morning", "Tomorrow evening", "This weekend"];

const InsuranceDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // applicant form
    const [fullName, setFullName] = useState("");
    const [dob, setDob] = useState("");
    const [gender, setGender] = useState("male");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [pincode, setPincode] = useState("");
    const [family, setFamily] = useState([]);
    const [callTime, setCallTime] = useState(CALL_TIMES[0]);

    useEffect(() => {
        setLoading(true);
        api.get(`/insurance-plans/${slug}`).then((r) => setPlan(r.data)).catch(() => setPlan(null)).finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (!user) return;
        setFullName(user.full_name || "");
        setPhone(user.phone || "");
        setEmail(user.email || "");
        const def = (user.addresses || []).find((a) => a.is_default) || (user.addresses || [])[0];
        if (def?.pincode) setPincode(def.pincode);
    }, [user]);

    const addMember = () => setFamily((f) => [...f, { name: "", dob: "", relation: "spouse" }]);
    const updMember = (i, k, v) => setFamily((f) => f.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
    const rmMember = (i) => setFamily((f) => f.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
        if (!fullName.trim() || !dob || !phone.trim() || !email.trim() || !pincode.trim()) {
            toast.error("Fill all required fields"); return;
        }
        if (family.some((m) => !m.name.trim() || !m.dob)) { toast.error("Complete each family member"); return; }
        setSubmitting(true);
        try {
            await api.post("/insurance-leads", {
                plan_id: plan.id,
                applicant: {
                    full_name: fullName.trim(), dob, gender, phone: phone.trim(),
                    email: email.trim().toLowerCase(), pincode: pincode.trim(),
                    family_members: family,
                },
                preferred_call_time: callTime,
            });
            toast.success("Request received! An advisor will reach out shortly.");
            navigate("/insurance");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not submit request");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Loading…</main></div>);
    if (!plan) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Plan not found. <Link to="/insurance" className="text-brand-700 hover:underline">Browse plans</Link></main></div>);

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-4xl px-6 py-10 lg:px-12" data-testid="insurance-detail">
                <Link to="/insurance" className="text-xs text-zinc-500 hover:underline">← All insurance plans</Link>
                <div className="mt-3 grid gap-6 lg:grid-cols-3">
                    {/* Left: details */}
                    <div className="lg:col-span-2 space-y-6">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-6 dark:bg-white/[0.03] dark:border-white/10">
                            <Shield className="h-6 w-6 text-brand-700" />
                            <p className="mt-3 text-xs uppercase tracking-wider text-zinc-500">{plan.provider_name}</p>
                            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{plan.plan_name}</h1>
                            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                                <div><p className="text-xs text-zinc-500">Cover</p><p className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{lakh(plan.cover_amount)}</p></div>
                                <div><p className="text-xs text-zinc-500">Premium</p><p className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{inr(plan.premium_yearly)}<span className="text-xs text-zinc-500">/yr</span></p></div>
                                <div><p className="text-xs text-zinc-500">Age range</p><p className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{plan.age_range}</p></div>
                            </div>
                        </section>

                        <section>
                            <p className="label-uppercase text-zinc-500">Key benefits</p>
                            <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="insurance-benefits-list">
                                {(plan.key_benefits || []).map((b, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{b}
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {Array.isArray(plan.exclusions) && plan.exclusions.length > 0 && (
                            <section>
                                <p className="label-uppercase text-zinc-500">Exclusions</p>
                                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                                    {plan.exclusions.map((b, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-zinc-500">
                                            <XIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />{b}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>

                    {/* Right: lead CTA */}
                    <aside className="lg:sticky lg:top-24 self-start">
                        {!showForm ? (
                            <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-5 dark:bg-brand-950/20 dark:border-white/10">
                                <p className="label-uppercase text-brand-700">Get a personalised quote</p>
                                <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">Our advisor will call you with the best price and finalize the policy.</p>
                                <button onClick={() => setShowForm(true)} className="btn-brand mt-4 w-full" data-testid="insurance-show-form-btn">Request a callback</button>
                                <p className="mt-2 text-[11px] text-zinc-500">No payment required to get a quote.</p>
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10" data-testid="insurance-lead-form">
                                <p className="label-uppercase text-zinc-500">Your details</p>
                                <div className="mt-3 space-y-2">
                                    <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name *" className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-name" />
                                    <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} placeholder="Date of birth *" className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-dob" />
                                    <select value={gender} onChange={(e) => setGender(e.target.value)} className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-gender">
                                        <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                                    </select>
                                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone *" className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-phone" />
                                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email *" type="email" className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-email" />
                                    <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Pincode *" maxLength={10} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-applicant-pincode" />
                                </div>

                                {/* Family members */}
                                <div className="mt-4">
                                    <p className="text-xs font-medium text-zinc-500">Family members (optional)</p>
                                    {family.map((m, i) => (
                                        <div key={i} className="mt-2 space-y-1 rounded-md border border-zinc-100 p-2 dark:border-white/10" data-testid={`insurance-family-${i}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <input value={m.name} onChange={(e) => updMember(i, "name", e.target.value)} placeholder="Name" className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-xs focus:border-brand-400 focus:outline-none" />
                                                <button onClick={() => rmMember(i)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                            <div className="flex gap-2">
                                                <input type="date" value={m.dob} onChange={(e) => updMember(i, "dob", e.target.value)} className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-xs focus:border-brand-400 focus:outline-none" />
                                                <select value={m.relation} onChange={(e) => updMember(i, "relation", e.target.value)} className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs focus:border-brand-400 focus:outline-none">
                                                    <option value="spouse">Spouse</option>
                                                    <option value="child">Child</option>
                                                    <option value="parent">Parent</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                    <button onClick={addMember} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline" data-testid="insurance-add-family-btn">
                                        <Plus className="h-3 w-3" /> Add family member
                                    </button>
                                </div>

                                <div className="mt-4">
                                    <p className="text-xs font-medium text-zinc-500">Preferred call time</p>
                                    <select value={callTime} onChange={(e) => setCallTime(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="insurance-call-time">
                                        {CALL_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>

                                <button onClick={submit} disabled={submitting} className="btn-brand mt-4 w-full disabled:opacity-50" data-testid="insurance-submit-lead-btn">
                                    {submitting ? "Submitting…" : "Submit request"}
                                </button>
                                <button onClick={() => setShowForm(false)} className="mt-2 w-full text-xs text-zinc-500 hover:underline">Cancel</button>
                            </div>
                        )}
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default InsuranceDetail;
